import { Box, Button, Tab, Tabs } from "@mui/material";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useDeleteCultureCategoryMutation,
  useEditCultureCategoryMutation,
  useGetCultureCategoriesQuery,
} from "../../api/apiSlice";
import { ConfirmDialog, CustomTabPanel, Table } from "../../components";
import { Drawer } from "../../components/Drawer/Drawer";
import { useHasPermission } from "../../hooks";
import { appendSearchParams, getApiErrorMessage } from "../../utils";
import { PageTabParams } from "../../utils/constants";
import { UserPermissions } from "../../utils/permissions";
import { ICultureCategory, IPreviewCultureCategory, ModalType, TabKeyEnum, TabLabelEnum } from "../../utils/types";
import { CreateOrEditCultureCategory } from "./CreateOrEditCultureCategory";
import { useGetCultureCategoriesTableColumns } from "./useGetCultureCategoriesTableColumns";
import { ViewCultureCategory } from "./ViewCultureCategory";

export const CultureCategories = () => {
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const location = useLocation();
  const navigate = useNavigate();

  const [urlParams] = React.useState(new URLSearchParams(location.search));
  const [hoveredRow, setHoveredRow] = useState(-1);
  const [activeDrawer, setActiveDrawer] = useState<ModalType>("Create");
  const [activeCategory, setActiveCategory] = useState<IPreviewCultureCategory | null>(null);
  const [isUnpublishDialogOpen, setIsUnpublishDialogOpen] = React.useState(false);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = React.useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = React.useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [sorting, setSorting] = React.useState([{ id: "updatedAt", desc: true }]);

  const [editCategory, editResult] = useEditCultureCategoryMutation();
  const [deleteCategory, deleteResult] = useDeleteCultureCategoryMutation();

  const canViewUnpublishedCategories = useHasPermission(UserPermissions.VIEW_UNPUBLISHED_CULTURE_CATEGORIES);
  const canViewReadyToReviewCategories = useHasPermission(UserPermissions.VIEW_READY_TO_REVIEW_CULTURE_CATEGORIES);

  const visibleTabs = PageTabParams.filter(({ value }) => {
    if (value === TabKeyEnum.Archived) return false;
    if (value === TabKeyEnum.Unpublished && !canViewUnpublishedCategories) return false;
    if (value === TabKeyEnum.ReadyForReview && !canViewReadyToReviewCategories) return false;
    return true;
  });

  const rawTabParam = (urlParams.get("activeTab") ?? visibleTabs[0]?.value ?? PageTabParams[0].value) as TabKeyEnum;
  const activeTabParam = visibleTabs.some(({ value }) => value === rawTabParam) ? rawTabParam : visibleTabs[0].value;
  const activeTabValue = Math.max(
    0,
    visibleTabs.findIndex(({ value }) => value === activeTabParam),
  );

  const apiSupportedParams = visibleTabs[activeTabValue].params;
  const { data, isLoading, isFetching } = useGetCultureCategoriesQuery({
    ...apiSupportedParams,
    name: filter || undefined,
    page: pagination.pageIndex + 1,
    per_page: pagination.pageSize,
    expand: "createdBy,updatedBy",
    sortBy: (sorting[0] ?? { id: "updatedAt", desc: true }).id,
    sortOrder: (sorting[0] ?? { id: "updatedAt", desc: true }).desc ? "DESC" : "ASC",
  });

  const categoriesData = React.useMemo(
    () =>
      (data?.data ?? []).map((item: ICultureCategory) => ({
        ...item,
        totalCultures: item.totalCultures ?? 0,
        createdBy: item.createdBy?.name,
        updatedBy: item.updatedBy?.name,
        published:
          item.published === 0
            ? TabLabelEnum.Unpublished
            : item.published === -1
              ? TabLabelEnum.ReadyForReview
              : TabLabelEnum.Published,
      })),
    [data],
  );

  const total = data?.total || 0;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const activeTab = visibleTabs[newValue].value;
    const newUrlParams = appendSearchParams({ activeTab }, urlParams);
    navigate(`${location.pathname}?${newUrlParams.toString()}`, {
      replace: true,
    });
  };

  const handleEditCategory = () => {
    setActiveDrawer("Edit");
    setIsDrawerOpen(true);
  };

  const openCreateDialog = () => {
    setHoveredRow(-1);
    setActiveCategory(null);
    setActiveDrawer("Create");
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setActiveCategory(null);
  };

  const handlePreviewCategory = () => {
    setActiveDrawer("Preview");
    setIsDrawerOpen(true);
  };

  const columns = useGetCultureCategoriesTableColumns({
    activeTab: activeTabParam,
    setHoveredRow,
    hoveredRow,
    handlePreviewCategory,
    handleEditCategory,
    handleUnpublishCategory: () => setIsUnpublishDialogOpen(true),
    handlePublishCategory: () => setIsPublishDialogOpen(true),
    handleRemoveCategory: () => setIsRemoveDialogOpen(true),
  });

  const handleRemoveCategory = () => {
    if (activeCategory?.id) {
      deleteCategory({ id: activeCategory.id })
        .unwrap()
        .then(() => {
          setIsRemoveDialogOpen(false);
          toast.success("Category removed successfully");
        })
        .catch(error => {
          const errorMessage = getApiErrorMessage(error, "Failed to remove category");
          toast.error(errorMessage);
        });
    }
  };

  const handlePublishChange = (published: TabKeyEnum) => {
    const messages = {
      [TabKeyEnum.Published]: {
        value: 1,
        success: "Category published successfully",
        error: "Failed to publish category",
      },
      [TabKeyEnum.ReadyForReview]: {
        value: -1,
        success: "Category saved successfully",
        error: "Failed to save category",
      },
      [TabKeyEnum.Unpublished]: {
        value: 0,
        success: "Category unpublished successfully",
        error: "Failed to unpublish category",
      },
      [TabKeyEnum.Archived]: {
        value: 0,
        success: "Category archived successfully",
        error: "Failed to archive category",
      },
    };

    if (activeCategory) {
      editCategory({
        id: activeCategory.id,
        published: messages[published].value,
        name: activeCategory.name,
        description: activeCategory.description ?? undefined,
        icon: activeCategory.icon ?? undefined,
      })
        .unwrap()
        .then(() => {
          setIsUnpublishDialogOpen(false);
          setIsPublishDialogOpen(false);
          setIsReviewDialogOpen(false);
          handleDrawerClose();
          toast.success(messages[published].success);
        })
        .catch(error => {
          const errorMessage = getApiErrorMessage(error, messages[published].error);
          toast.error(errorMessage);
        });
    }
  };

  const canCreateCategory = useHasPermission(UserPermissions.CREATE_CULTURE_CATEGORIES);

  return (
    <>
      <Drawer
        title={`${activeDrawer} Culture Category`}
        onClose={handleDrawerClose}
        open={isDrawerOpen}
        sx={{ width: "500px", height: "100%" }}
      >
        {isDrawerOpen && (activeDrawer === "Create" || activeDrawer === "Edit") && (
          <CreateOrEditCultureCategory
            activeTab={activeTabParam}
            handleClose={handleDrawerClose}
            category={activeCategory}
            isEditing={activeDrawer === "Edit"}
          />
        )}

        {isDrawerOpen && activeDrawer === "Preview" && (
          <ViewCultureCategory
            activeTab={activeTabParam}
            handleClose={handleDrawerClose}
            handleEditCategory={handleEditCategory}
            handleReviewCategory={() => setIsReviewDialogOpen(true)}
            category={activeCategory}
            handlePublishCategory={() => setIsPublishDialogOpen(true)}
            handleUnpublishCategory={() => setIsUnpublishDialogOpen(true)}
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={isUnpublishDialogOpen}
        title={`Unpublish Category: ${activeCategory?.name}`}
        body="Are you sure you want to unpublish this category?"
        onClose={() => setIsUnpublishDialogOpen(false)}
        onConfirm={() => handlePublishChange(TabKeyEnum.Unpublished)}
        loading={editResult.isLoading}
      />

      <ConfirmDialog
        open={isPublishDialogOpen}
        title={`Publish Category: ${activeCategory?.name}`}
        body="Are you sure you want to publish this category?"
        onClose={() => setIsPublishDialogOpen(false)}
        onConfirm={() => handlePublishChange(TabKeyEnum.Published)}
        loading={editResult.isLoading}
      />

      <ConfirmDialog
        open={isReviewDialogOpen}
        title={`Save Category: ${activeCategory?.name}`}
        body="Are you sure you want to save this category?"
        onClose={() => setIsReviewDialogOpen(false)}
        onConfirm={() => handlePublishChange(TabKeyEnum.ReadyForReview)}
        loading={editResult.isLoading}
      />

      <ConfirmDialog
        open={isRemoveDialogOpen}
        title={`Remove Category: ${activeCategory?.name}`}
        body="Are you sure you want to remove this category? This action is permanent and cannot be undone."
        onClose={() => setIsRemoveDialogOpen(false)}
        onConfirm={handleRemoveCategory}
        loading={deleteResult.isLoading}
      />

      <Box display="flex" justifyContent="space-between">
        <Tabs value={activeTabValue} onChange={handleTabChange}>
          {visibleTabs.map(value => (
            <Tab key={value.label} label={value.label} sx={{ p: 2, mr: 2 }} />
          ))}
        </Tabs>
        {canCreateCategory && (
          <Box>
            <Button onClick={openCreateDialog} sx={{ borderRadius: 0.5 }} color="primary" variant="outlined">
              CREATE CATEGORY
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ width: "100%" }}>
        <CustomTabPanel value={activeTabValue} index={activeTabValue}>
          <Table
            columns={columns}
            data={categoriesData}
            manualPagination
            onPaginationChange={setPagination}
            manualFiltering
            onGlobalFilterChange={setFilter}
            manualSorting
            enableMultiSort={false}
            onSortingChange={setSorting}
            state={{ pagination, sorting }}
            rowCount={total}
            loading={isLoading || isFetching}
            muiTableBodyRowProps={({ row }: { row: { id: string; index: number; original: IPreviewCultureCategory } }) => ({
              onMouseEnter: () => {
                setHoveredRow(row.index);
                setActiveCategory(row.original);
              },
              onMouseLeave: () => {
                setHoveredRow(-1);
              },
              onClick: () => {
                handlePreviewCategory();
              },
            })}
          />
        </CustomTabPanel>
      </Box>
    </>
  );
};
